import { useEffect, useState } from "react";
import ImagePlaceholder from "../../../assets/images/dapps/imagePlaceholder.png";
import { SubmitHandler, useForm } from "react-hook-form";
import { fileApi } from "../../../services/files";
import { Close } from "../../../assets/icons/Close";
import ImageIcon from "../../../assets/icons/ImageIcon";
import { ProjectFormInput } from "../../DApps/components/Pannel/components/ProjectFormInput";
import { Button } from "../../../components/Button";
import { CheckIcon } from "../../../assets/icons/Check";
import { INew } from "../types";
import { newsApi } from "../service";
import { MAX_NEWS_DESCRIPTION_LENGTH, MIN_IMG_FILE_SIZE } from "../constants";
import { useNewsStore } from "../store";

export const NewsEditForm = ({
  newToEdit,
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
  newToEdit?: INew;
}) => {
  const [imagePreview] = useState<string>(newToEdit?.image || ImagePlaceholder);
  const [imageFile, setImage] = useState<File | null>(null);
  const [imageSizeError, setImageSizeError] = useState(false);

  const { isAddLoading } = useNewsStore();
  const { register, handleSubmit, watch, control, setValue, formState } =
    useForm<INew>({
      defaultValues: {
        id: newToEdit?.id || "",
        title: newToEdit?.title || "",
        description: newToEdit?.description || "",
        image: newToEdit?.image || "",
        link: newToEdit?.link || "",
      },
      mode: "all",
    });

  const image = watch("image");

  const onSubmit: SubmitHandler<INew> = async (data) => {
    await newsApi.editNew(data, async () => {
      onClose();
    });
  };

  const isSubmitDisabled =
    !watch("title") &&
    !watch("description") &&
    !watch("image") &&
    !watch("link");

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      if (file.size > MIN_IMG_FILE_SIZE) {
        setImageSizeError(true);
        return;
      }
      setImageSizeError(false);
      setImage(file);
    }
  };

  const uploadImage = async (file: File) => {
    const url = await fileApi.uploadImage(file);
    setValue("image", url);
  };

  useEffect(() => {
    imageFile && uploadImage(imageFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageFile]);

  useEffect(() => {
    if (imageSizeError) {
      const timer = setTimeout(() => setImageSizeError(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [imageSizeError]);

  return (
    <>
      {isOpen && (
        <div
          className="fixed z-9 bg-black/40 top-0 left-0 right-0 bottom-0 w-full"
          onClick={onClose}
        />
      )}

      <div
        className={`fixed w-full md:w-[487px] h-[100svh] overflow-y-auto custom-scrollbar px-[20px] md:px-[40px] pt-[60px] top-0 bottom-0 right-0 bg-black md:border-l-[1px] z-10 transform transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex text-[24px] font-[700] leading-[32px] justify-between items-center mb-[40px]">
          <h3>Edit new</h3>
          <button onClick={onClose}>
            <Close />
          </button>
        </div>

        <form
          id="news-edit-form"
          onSubmit={handleSubmit(onSubmit)}
          className={`flex flex-col gap-[24px] h-fit`}
        >
          <div className="flex gap-[24px] w-full">
            <div className="min-w-[80px] md:min-w-[108px] max-w-[80px] md:max-w-[108px] h-[80px] md:h-[108px] overflow-hidden custom-scrollbar flex justify-center items-center bg-[#FFFFFF1A]">
              <img
                src={image ?? imagePreview}
                alt=""
                className="w-full h-full object-cover object-center"
              />
            </div>

            <div className="flex flex-col gap-[8px] justify-center">
              <input
                id="logo"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <label
                htmlFor="logo"
                className="w-[190px] h-[43px] bg-[#FFFFFF1A] flex items-center justify-center rounded-full"
              >
                <button
                  type="button"
                  className="flex items-center justify-center gap-[10px] text-[14px] font-[500] leading-[18.9px]"
                  onClick={() => document.getElementById("logo")?.click()}
                >
                  <ImageIcon />
                  Upload logo for new
                </button>
              </label>
              <p
                className={`text-[12px] font-[500] text-center ${imageSizeError ? "text-red-500" : "opacity-70"}`}
              >
                {imageSizeError
                  ? "Maximum image size 5 MB"
                  : "max 5mb, .jpeg, .png"}
              </p>
            </div>
          </div>

          <ProjectFormInput
            title="New title"
            placeholder="Add title here"
            control={control}
            name="title"
          />

          <div className="flex flex-col gap-[4px]">
            <p className="ml-1 text-[12px] leading-[17px] font-[500] text-[#B5B2C9]">
              Description of the new
            </p>

            <div className="relative">
              <textarea
                placeholder="Add description here"
                className="bg-[#FFFFFF1A] py-[15.5px] px-[20px] rounded-[8px] flex items-center min-h-[103px] max-h-[103px] w-full text-[14px] leading-[17px] font-[500] placeholder:opacity-50 placeholder:text-[#fff] custom-scrollbar"
                style={{
                  outline: "none",
                  resize: "none",
                  border: `1px solid ${formState.errors.description ? "#FF4D4D" : "transparent"}`,
                }}
                {...register("description", {
                  maxLength: {
                    message: `Max ${MAX_NEWS_DESCRIPTION_LENGTH} characters`,
                    value: MAX_NEWS_DESCRIPTION_LENGTH,
                  },
                })}
              />

              <p className="absolute bottom-[12px] right-[20px] py-[4px] px-[6px] bg-[#3f3f3f] rounded-[8px] text-[#FFFFFF80]">
                {watch("description")?.length || 0}
              </p>
            </div>

            {formState.errors.description && (
              <p className="text-[12px] leading-[17px] font-[500] text-[#FF4D4D]">
                {formState.errors.description.message}
              </p>
            )}
          </div>

          <ProjectFormInput
            title="Link to the source of the new"
            control={control}
            name="link"
            placeholder={"Optional"}
          />
        </form>

        <div className="bg-gradient-to-t from-black to-transparent pb-[40px] pt-[60px] flex items-center w-full left-0 right-0 z-10">
          <Button
            extraClass="w-full flex justify-center items-center gap-[10px]"
            type="submit"
            form="news-edit-form"
            disabled={isAddLoading || isSubmitDisabled}
          >
            <CheckIcon className="w-[16px] h-[16px] mt-[2px]" />
            Save new
          </Button>
        </div>
      </div>
    </>
  );
};
